/**
 * Vue template configuration shape
 */
import { TemplateConfiguration, TemplateEngine, TemplateType } from '../types';

export interface VueTemplateConfiguration extends TemplateConfiguration {
    framework: TemplateEngine.VUE;
    type: TemplateType;
    default_version: string;
    versions: VueTemplateVersionInfo[];
}

/**
 * Vue template version info shape
 */
export interface VueTemplateVersionInfo {
    id: string;
    version: string;
    type: TemplateType;
    html: string | null;
    css: string | null;
    js: string | null;
}

/**
 * @see {@link https://v2.vuejs.org/v2/guide/components-props.html#Prop-Types}
 */
export type VuePropTypes =
    | StringConstructor
    | NumberConstructor
    | BooleanConstructor
    | ArrayConstructor
    | ObjectConstructor
    | DateConstructor
    | FunctionConstructor
    | SymbolConstructor;

export enum VueLifecycleHook {
    BEFORE_CREATE = 'beforeCreate',
    CREATED = 'created',
    BEFORE_MOUNT = 'beforeMount',
    MOUNTED = 'mounted',
    BEFORE_UPDATE = 'beforeUpdate',
    UPDATED = 'updated',
    BEFORE_DESTROY = 'beforeDestroy',
    DESTROYED = 'destroyed',
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type VueInjectedMethods = {
    methods: {
        [methodName: string]: (...args: any[]) => unknown;
    };
    lifeCycleHooks: {
        [VueLifecycleHook.BEFORE_CREATE]?: (...args: any[]) => unknown;
        [VueLifecycleHook.CREATED]?: (...args: any[]) => unknown;
        [VueLifecycleHook.BEFORE_MOUNT]?: (...args: any[]) => unknown;
        [VueLifecycleHook.MOUNTED]?: (...args: any[]) => unknown;
        [VueLifecycleHook.BEFORE_UPDATE]?: (...args: any[]) => unknown;
        [VueLifecycleHook.UPDATED]?: (...args: any[]) => unknown;
        [VueLifecycleHook.BEFORE_DESTROY]?: (...args: any[]) => unknown;
        [VueLifecycleHook.DESTROYED]?: (...args: any[]) => unknown;
    };
};
/* eslint-enable @typescript-eslint/no-explicit-any */

//todo: Determine if these docs need to be cleaned up / converted to types
// or if the Vue docs should be linked instead
/**
 * LifeCycleHooks
 * @typedef {object} LifeCycleHooks
 * @property {lifeCycleBeforeCreateCallback} [beforeCreate] -
 * data not reactive yet - can't access data with this.x yet - events not set
 * yet
 * @property {lifeCycleCreatedCallback} [created] - events and
 * data observation are set now - events are active and access to reactive data
 * is enabled - templates not yet mounted or rendered
 * @property {lifeCycleBeforeMountCallback} [beforeMount] -
 * invoked after our template has been compiled and our virtual DOM updated by
 * Vue After this, the $el property is added to the Vue instance, the native DOM
 * is updated and the mounted() method is invoked
 * @property {lifeCycleMountedCallback} [mounted] - gives you
 * access to templates and enables interaction with the DOM
 * @property {lifeCycleBeforeUpdateCallback} [beforeUpdate] -
 * This method runs after data changes on your component and the update cycle
 * begins right before the DOM is patched and re-rendered
 * @property {lifeCycleUpdatedCallback} [updated] - The
 * updated() method runs after data changes on your component and the DOM
 * re-renders.
 * @property {lifeCycleBeforeDestroyCallback} [beforeDestroy] -
 * This method is executed right before your component is destroyed. Here your
 * component is still fully present and functional. If you need to cleanup
 * events, beforeDestroy() is the best time to do that
 * @property {lifeCycleDestroyedCallback} [destroyed] - This
 * method is executed right before your component is destroyed. Here your
 * component is still fully present and functional. If you need to cleanup
 * events, beforeDestroy() is the best time to do that.
 *
 * This method is called after your component has been destroyed, its directives
 * have been unbound and its event listeners have been removed. The destroyed()
 * method can be used to do any last minute cleanup or informing a remote server
 * that the component was destroyed.
 */

/**
 * Called synchronously immediately after the instance has been initialized,
 * before data observation and event/watcher setup.
 * @callback lifeCycleBeforeCreateCallback
 */

/**
 * Called synchronously after the instance is created
 * @callback lifeCycleCreatedCallback
 */

/**
 * Called right before the mounting begins
 * @callback lifeCycleBeforeMountCallback
 */

/**
 * Called after the instance has been mounted
 * @callback lifeCycleMountedCallback
 */

/**
 * Called when data changes, before the DOM is patched
 * @callback lifeCycleBeforeUpdateCallback
 */

/**
 * Called after a data change causes the virtual DOM to be re-rendered and
 * patched.
 * @callback lifeCycleUpdatedCallback
 */

/**
 * Called right before a Vue instance is destroyed. At this stage the instance
 * is still fully functional.
 * @callback lifeCycleBeforeDestroyCallback
 */

/**
 * Called after a Vue instance has been destroyed.
 * @callback lifeCycleDestroyedCallback
 */
